import GetState from '../GetState';
import { QuestRoute } from '../api/QuestRoutes';
import Table from '../table/Table';
import { IsAuthorized, userRoles } from '../RoleProtected';
import Page from '../layout/Page';
import { generateId, shortHandDateTime} from '../utility/functions';
import LinkTo from '../forms/LinkTo';
import ManagementRow from '../table/ManagementRow';
import Button from '../forms/Button';
import Flag from './plateReads/Flag';
import Row from '../layout/Row';
import AddNewCustomer from '../processors/AddNewCustomer';
import ModalFactory from '../Modal';
import HamQuest from '../api/HamQuestGiver';
import { useContext, useEffect, useRef, useState } from 'react';
import useDebounce from '../utility/debounce';
import Input from '../forms/Input';
import ToggleSwitch from '../forms/ToggleSwitch';
import { UserContexts } from '../stateProvider/UsersContext';
import AssignPlateReadAwardForm from '../processors/AssignPlateReadAwardForm';
import AddPlateReadMessageForm from '../processors/AddPlateReadMessageForm';
import RequireFeature, { Features } from '../RequireFeature';
import { useNavigate } from 'react-router-dom';

const VisitorsReport = ({ ...props }) => {
    const canViewCustomers = IsAuthorized(userRoles.viewCustomers);
    const navigate = useNavigate();

    const [visitors, refreshVisitors] = GetState(
        QuestRoute.plateReads.vistors
    );

    const [totalVisitsGt, setTotalVisitsGt] = useState('');
    const [totalVisitsLt, setTotalVisitsLt] = useState('');
    const [lastVisitGt, setLastVisitGt] = useState('');
    const [lastVisitLt, setLastVisitLt] = useState('');
    const [showVisitsInRange, setShowVisitsInRange] = useState(false);

    const debouncedTotalVisitsGt = useDebounce(totalVisitsGt, 600);
    const debouncedTotalVisitsLt = useDebounce(totalVisitsLt, 600);
    const debouncedLastVisitGt = useDebounce(lastVisitGt, 600);
    const debouncedLastVisitLt = useDebounce(lastVisitLt, 600);

    const filterMounted = useRef(false);

    useEffect(() => {
        if (!filterMounted.current) {
            filterMounted.current = true;
            return;
        }
        const params = new URLSearchParams();
        if (debouncedTotalVisitsGt) params.set('totalVisitsGt', debouncedTotalVisitsGt);
        if (debouncedTotalVisitsLt) params.set('totalVisitsLt', debouncedTotalVisitsLt);
        if (debouncedLastVisitGt) params.set('lastVisitGt', debouncedLastVisitGt);
        if (debouncedLastVisitLt) params.set('lastVisitLt', debouncedLastVisitLt);
        params.set('showVisitsInRange', showVisitsInRange);
        const query = params.toString();
        refreshVisitors(QuestRoute.plateReads.vistors + (query ? `?${query}` : ''));
    }, [debouncedTotalVisitsGt, debouncedTotalVisitsLt, debouncedLastVisitGt, debouncedLastVisitLt, showVisitsInRange]);

    const userContext = useContext(UserContexts);
    const userContextRef = useRef(userContext);

    useEffect(() => {
    userContextRef.current = userContext;
    }, [userContext]);

    const createCustomerAccount = async (plate) => {               
        const modalId = generateId();
        const callBack = () => updatePlateReadsCustomerIdCallback();
        
        ModalFactory.show({
            id: modalId,
            title: 'New Customer',
            type: 'info',
            body: <AddNewCustomer modalId={modalId} license={plate} callback={callBack} />            
        });
    }

    const updatePlateReadsCustomerIdCallback = () => {
        const customerId = userContextRef.current.user.customerId;
        return new Promise(async (resolve, reject) => {
                await HamQuest.post(QuestRoute.plateReads.updateCustomer, {customerId})
                .then(resolve(true))
                .catch(reject({ error: { errorMessage: 'Error updating PlateRead records for new customer.' } }))
                .finally(() => refreshVisitors())
        })        
    }

    const tableConfig = {
        tableId: 'visitor',
        data: visitors,
        colSpacing: [10, 15],
        headers: [
            {
                label: 'Plate',
                propKey: 'plate',
            }, {
                label: showVisitsInRange ? 'Visits In Range' : 'Total Visits',
                propKey: 'totalVisits'
            }, {
                label: 'Last Visit',
                propKey: 'lastVisit'
            }, {
                label: 'Primary Location',
                propKey: 'primaryLocation'
            }, {
                label: 'Customer Name',
                propKey: 'customerName'
            }, {
                label: 'Account Status',
                propKey: 'accountStatus'
            }, {
                label: 'Alerts',
                propKey: 'flags',       
            }
        ],
        quickSort: [
            {
                label: 'Visitors',
                propKey: 'customerName',
                query: /^$/,    //finds all that have no customer name, meaning they are just visitors.
                color: '#21D59B',
                defaultSearch: true
            }, {
                label: 'Customers',
                propKey: 'customerName',
                query: /.+/i,   // Matches all that have a customer name.
                color: '#10BBF3'
            }, {
                label: 'Club Status',
                propKey: 'accountStatus',
                query: 'Active',
                color: '#93090e'
            }
        ],
        controls: [
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5em', width: '100%' }}>
                <Row wrap>
                    <ToggleSwitch
                        key='visitorDateModeSwitch'
                        options={[
                            {
                                label: 'Last Visit',
                                value: false,
                            }, {
                                label: 'Visits In Range',
                                value: true,
                            }
                        ]}
                        value={showVisitsInRange}
                        onClick={() => setShowVisitsInRange(v => !v)}
                    />
                    <Input
                        standalone
                        label='Min Visits'
                        name='totalVisitsGt'
                        type='number'
                        min='0'
                        placeholder='≥'
                        value={totalVisitsGt}
                        onChange={(e) => setTotalVisitsGt(e.target.value)}
                        flex='0.5'
                    />
                    <Input
                        standalone
                        label='Max Visits'
                        name='totalVisitsLt'
                        type='number'
                        min='0'
                        placeholder='≤'
                        value={totalVisitsLt}
                        onChange={(e) => setTotalVisitsLt(e.target.value)}
                        flex='0.5'
                    />
                    <Input
                        standalone
                        label={showVisitsInRange ? 'Range Start' : 'Last Visit After'}
                        name='lastVisitGt'
                        type='date'
                        value={lastVisitGt}
                        onChange={(e) => setLastVisitGt(e.target.value)}
                        flex='0.7'
                    />
                    <Input
                        standalone
                        label={showVisitsInRange ? 'Range End' : 'Last Visit Before'}
                        name='lastVisitLt'
                        type='date'
                        value={lastVisitLt}
                        onChange={(e) => setLastVisitLt(e.target.value)}
                        flex='0.7'
                    />
                </Row>

                <Row wrap>
                    <Button 
                        onClick={() =>{
                            const id = generateId();
                            const platesList = window.visitorTableData.map(visitor => visitor.plate);  
                            ModalFactory.show(window.visitorTableData.length ? {
                                id,
                                title: 'Assign Plate Read Award',
                                body: <AssignPlateReadAwardForm modalId={id} platesList={platesList} />
                            } : {
                                id,
                                title: 'No Users',
                                body: 'No users selected, please have at least one user.',
                                type: 'error'
                            })
                        }}
                        label='Plate Award'
                    />
                    <Button
                        onClick={() => {
                            const id = generateId();
                            const platesList = window.visitorTableData.map(visitor => visitor.plate);  
                            ModalFactory.show(window.visitorTableData.length ? {
                                id,
                                title: 'Add Plate Read Message',
                                body: <AddPlateReadMessageForm modalId={id} platesList={platesList} />
                            } : {
                                id,
                                title: 'No Users',
                                body: 'No users selected, please have at least one user.',
                                type: 'error'
                            })                    
                        }}
                        label='Plate Message'
                    />
                </Row>
            </div>
        ],     
        renderFormat: (e) => (
            <ManagementRow
                key={generateId()}
                layout='elipsis'
                controls={[
                    <Button key={generateId()} icon='user' onClick={() => createCustomerAccount(e.plate, e.transRecordId)} label='Make Customer Account'/>
                ]}
            >
                <td>
                    {canViewCustomers ?      
                        <LinkTo 
                        to={'/visitor/' + e.plate}>{e.plate}</LinkTo> : <div>{e.plate}</div>}
                </td>               
                <td>{e.totalVisits}</td>
                <td>{shortHandDateTime(e.lastVisit)}</td>
                <td>{e.primaryLocation}</td>
                <td>{e.customerName || 'Visitor'}</td>
                <td>{e.accountStatus}</td>
                <td>
                    <Row>
                        {e.flags.map((flag) => (<Flag key={generateId()} flag={flag} />))}
                    </Row>
                </td>
            </ManagementRow>
        )
    }

    return (
        <RequireFeature requiredFeatures={Features.PlateReads}>
            <Page
                title='Visitors Report'
                icon='mask'
                controls={<Button onClick={() => navigate('/visitors')} label='Back to Visitors' />}
            >
                <Table config={tableConfig} />
            </Page>
        </RequireFeature>
    );
}

export default VisitorsReport;
